import { useInternalState, assignMswData, serializeQuery } from '../hooks/internalState';
import {
  DeleteVideoPrivateCommentReactionResult,
  DeleteVideoPrivateCommentReactionOptions,
  DeleteVideoPrivateCommentReactionFields,
  deleteVideoPrivateCommentReaction,
} from '../fetchers/video/private_comment/reaction';
import { useCallback } from 'react';
import { useSWRConfig } from 'swr';
import { useGctlConfig } from '../hooks/GctlProvider';

/**
 * DO NOT MODIFY
 * Auto-generated by gctl-plugin-vimeo-hooks
 */

type UseDeleteVideoPrivateCommentReactionOptions<
  Fields extends DeleteVideoPrivateCommentReactionFields,
> = DeleteVideoPrivateCommentReactionOptions<Fields>;

/**
 * Delete a clip comment reaction
 * This method deletes the specified clip comment reaction. The authenticated user or guest user must be the owner of the reaction.
 */

export function useDeleteVideoPrivateCommentReaction<
  Fields extends DeleteVideoPrivateCommentReactionFields,
>() {
  const { mutate } = useSWRConfig();
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<
    DeleteVideoPrivateCommentReactionResult<Fields> | undefined
  >();

  let call = useCallback(
    async (options: UseDeleteVideoPrivateCommentReactionOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await mutate(
          `/videos/${options.where.videoId}/private_comments/${options.where.commentId}/reactions/${
            options.where.reactionId
          }${serializeQuery(options)}`,
          deleteVideoPrivateCommentReaction({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
          }),
          false,
        );

        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [call, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useDeleteVideoPrivateCommentReaction, {
    endpoint: '/videos/:videoId/private_comments/:commentId/reactions/:reactionId',
    method: 'DELETE',
  });
}
